import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  CheckCircleIcon,
  DocumentTextIcon,
  PencilSquareIcon,
  PaperAirplaneIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ArrowPathIcon,
  EyeIcon,
  DocumentArrowDownIcon,
  CheckBadgeIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import DOMPurify from 'dompurify';
import { api } from '../services/authService';
import { useAuth } from '../contexts/AuthContext';
import { validateDocument } from '../utils/validators';
import CalculatedFields from '../components/contracts/CalculatedFields';
import ClauseSelector from '../components/contracts/ClauseSelector';
import ValidationChecklist from '../components/contracts/ValidationChecklist';
import ContractParties from '../components/contracts/ContractParties';
import PaymentInfo from '../components/contracts/PaymentInfo';
import SignersList from '../components/contracts/SignersList';
import GuaranteesWidget from '../components/contracts/GuaranteesWidget';
import SearchableSelect from '../components/SearchableSelect';

// Hook customizado para debounce
function useDebounce(value, delay) {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}

const ContractEditorPage = () => {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  // FIX: Detectar novo contrato pela URL, não pelo id (que é undefined em /contracts/new)
  const isNewContract = location.pathname === '/contracts/new';
  const isRegenerateMode = new URLSearchParams(location.search).get('regenerate') === '1';

  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [contractId, setContractId] = useState(isNewContract ? null : id);

  // Estado para edição direta do contrato (admin only)
  const [isEditingPreview, setIsEditingPreview] = useState(false);
  const [hasCustomHtml, setHasCustomHtml] = useState(false);
  const [savingCustomHtml, setSavingCustomHtml] = useState(false);
  const editablePreviewRef = useRef(null);

  // Dados do contrato
  const [contractData, setContractData] = useState({
    client_id: '',
    vehicle_id: '',
    start_date: '',
    end_date: '',
    monthly_rate: '',
    discount_percentage: 0,
    discount_amount: 0,
    deposit_percentage: 10,
    number_of_installments: 1,
    insurance_required: false,
    insurance_value: 0,

    // Dados personalizados - LOCADOR
    locador_razao_social: '',
    locador_cnpj: '',
    locador_endereco: '',
    locador_cidade: '',
    locador_estado: '',
    locador_cep: '',
    locador_representante_nome: '',
    locador_representante_cargo: '',
    locador_representante_cpf: '',
    locador_representante_rg: '',
    locador_representante_email: '',

    // Dados personalizados - LOCATÁRIO
    signatario_tipo: 'socio',
    locatario_representante_nome: '',
    locatario_representante_cargo: 'sócio administrador',
    locatario_representante_cpf: '',
    locatario_representante_rg: '',
    locatario_socios: [], // Array de sócios do cliente

    // Dados personalizados - PAGAMENTO E TESTEMUNHAS
    forma_pagamento: 'boleto bancário',
    dia_vencimento: '16',
    primeiro_vencimento: '',
    local_assinatura: 'Campo Grande/MS',
    testemunhas: [
      { nome: 'Abel Lopes de Andrade', cpf: '00586616209', email: 'abeldiretor@yahoo.com.br' },
      { nome: 'Maikon Batista de Andrade', cpf: '00586616209', email: 'maikonandrade37@gmail.com' }
    ],

    // Informações Complementares
    valor_por_mm_pneu: '120,00',
    veiculo_km_inicial: ''
  });

  const [clients, setClients] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [selectedClauses, setSelectedClauses] = useState([]);
  const [previewHtml, setPreviewHtml] = useState('');
  const [validation, setValidation] = useState({ valid: false, errors: [], warnings: [], checklist: [] });
  const [signers, setSigners] = useState([]);
  const [selectedEquipment, setSelectedEquipment] = useState([]);
  const [guarantees, setGuarantees] = useState({
    deposit_months: null,
    requires_guarantor: false
  });
  const [deadline, setDeadline] = useState(() => {
    // Default: 7 dias a partir de hoje
    const date = new Date();
    date.setDate(date.getDate() + 7);
    return date.toISOString().split('T')[0];
  });

  // Estados para preview em tempo real
  const [previewLoading, setPreviewLoading] = useState(false);
  const [autoPreviewEnabled, setAutoPreviewEnabled] = useState(true);
  const [lastPreviewTime, setLastPreviewTime] = useState(null);
  const previewRef = useRef(null);
  const lastCurrentStepRef = useRef(currentStep);
  const [downloadingDocx, setDownloadingDocx] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [showMinutaMenu, setShowMinutaMenu] = useState(false);
  const minutaMenuRef = useRef(null);

  // Debounce dos dados do contrato para preview em tempo real (1.5s de delay)
  const debouncedContractData = useDebounce(contractData, 1500);

  useEffect(() => {
    loadClients();
    loadVehicles();
    loadAllClauses(); // Carregar e pré-selecionar todas as cláusulas
    if (id && id !== 'new') {
      loadContract();
    }
  }, [id]);

  // Fechar menu da minuta ao clicar fora
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (minutaMenuRef.current && !minutaMenuRef.current.contains(e.target)) {
        setShowMinutaMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Efeito para preview em tempo real quando os dados do contrato mudam
  useEffect(() => {
    // Só gera preview automático se:
    // 1. Auto-preview está habilitado
    // 2. Estamos na etapa 2 (edição)
    // 3. Temos um ID de contrato válido
    // 4. Os dados essenciais estão preenchidos
    const useId = contractId || id;
    const hasEssentialData = debouncedContractData.client_id && debouncedContractData.vehicle_id;

    if (autoPreviewEnabled && currentStep === 2 && useId && hasEssentialData) {
      lastCurrentStepRef.current = currentStep;
      generatePreviewSilent(useId);
    } else {
      lastCurrentStepRef.current = currentStep;
    }
  }, [debouncedContractData, currentStep, autoPreviewEnabled, contractId, id]);

  // Função de preview silenciosa (sem toast)
  // REGRA: se existe custom_html salvo no banco, SEMPRE mostrá-lo.
  // O único jeito de voltar ao template é via "Restaurar template original".
  const generatePreviewSilent = async (explicitId = null) => {
    try {
      setPreviewLoading(true);
      const useId = explicitId || contractId || id;

      if (!useId) {
        return;
      }

      const response = await api.post(`/api/contracts/${useId}/generate-draft`, contractData);
      const data = response.data;
      if (!isEditingPreview) {
        if (data.customHtml) {
          setPreviewHtml(data.customHtml);
          console.log('✅ Preview usando edições salvas do banco');
        } else {
          setPreviewHtml(data.html);
        }
        setHasCustomHtml(!!data.customHtml);
      }
      setLastPreviewTime(new Date());
    } catch (error) {
      console.error('Erro ao gerar preview silencioso:', error);
    } finally {
      setPreviewLoading(false);
    }
  };

  const loadClients = async () => {
    try {
      const response = await api.get('/api/clients');
      const data = response.data;
      setClients(data.data || []);
    } catch (error) {
      console.error('Erro ao carregar clientes:', error);
      if (error.response?.status === 401) {
        toast.error('Sessão expirada. Faça login novamente.');
      }
    }
  };

  const loadVehicles = async () => {
    try {
      const editId = contractId || (id && id !== 'new' ? id : null);
      const params = editId ? { exclude_contract_id: editId } : {};
      const response = await api.get('/api/vehicles/available-by-period', { params });
      const data = response.data;
      setVehicles(data.data || []);
    } catch (error) {
      console.error('Erro ao carregar veículos:', error);
      if (error.response?.status === 401) {
        toast.error('Sessão expirada. Faça login novamente.');
      }
    }
  };

  const loadContractEquipment = async (equipmentIds) => {
    try {
      // Usar endpoint completo (não o de disponíveis) para incluir equipamentos já atribuídos
      const response = await api.get('/api/auxiliary-equipment/');
      const allEquipment = response.data.data || [];

      // Filtrar apenas os equipamentos que estão no contrato
      const contractEquipment = allEquipment
        .filter(eq => equipmentIds.includes(eq.id))
        .map(eq => ({
          equipment_id: eq.id,
          name: eq.name,
          description: eq.description,
          category: eq.category_name,
          monthly_rate: eq.monthly_rate,
          quantity: 1,
          max_quantity: Math.max(eq.quantity_available || 0, 1), // Permite pelo menos 1 (já está vinculado)
          total_amount: 0
        }));

      setSelectedEquipment(contractEquipment);
      console.log(`✅ Carregados ${contractEquipment.length} equipamento(s)`);
    } catch (error) {
      console.error('❌ Erro ao carregar equipamentos:', error);
    }
  };

  const loadContract = async () => {
    try {
      const response = await api.get(`/api/contracts/${id}`);
      const contract = response.data.contract;

      console.log('📄 Contrato carregado:', contract);

      // Substituir todo o contractData com os dados do contrato
      // O backend já retorna custom_data mesclado no objeto contract
      const loadedData = {
        client_id: contract.client_id || '',
        vehicle_id: contract.vehicle_id || '',
        start_date: contract.start_date ? contract.start_date.split('T')[0] : '',
        end_date: contract.end_date ? contract.end_date.split('T')[0] : '',
        monthly_rate: contract.monthly_rate || '',
        discount_percentage: contract.discount_percentage || 0,
        discount_amount: contract.discount_amount || 0,
        deposit_percentage: contract.deposit_percentage !== undefined ? parseFloat(contract.deposit_percentage) : 10,
        deposit_amount: contract.deposit_amount !== undefined ? parseFloat(contract.deposit_amount) : 0,
        number_of_installments: contract.number_of_installments || 1,
        insurance_required: contract.insurance_required || false,
        insurance_value: contract.insurance_value ? parseFloat(contract.insurance_value) : 0,
        pagamento_antecipado: contract.pagamento_antecipado || false,
        dias_antecipacao: contract.dias_antecipacao !== undefined ? parseInt(contract.dias_antecipacao) : 0,
        dia_vencimento_boleto: contract.dia_vencimento_boleto ? String(contract.dia_vencimento_boleto) : '10',

        // Dados personalizados (já vêm mesclados do backend)
        signatario_tipo: contract.signatario_tipo || 'socio',
        locatario_representante_nome: contract.locatario_representante_nome || '',
        locatario_representante_cargo: contract.locatario_representante_cargo || 'sócio administrador',
        locatario_representante_cpf: contract.locatario_representante_cpf || '',
        locatario_representante_rg: contract.locatario_representante_rg || '',
        // Parse locatario_socios se vier como string JSON
        locatario_socios: typeof contract.locatario_socios === 'string'
          ? JSON.parse(contract.locatario_socios)
          : (contract.locatario_socios || []),
        locador_representante_nome: contract.locador_representante_nome || '',
        locador_representante_cpf: contract.locador_representante_cpf || '',
        locador_representante_cargo: contract.locador_representante_cargo || '',
        locador_razao_social: contract.locador_razao_social || '',
        forma_pagamento: contract.forma_pagamento || 'boleto bancário',
        dia_vencimento: contract.dia_vencimento || '16',
        primeiro_vencimento: contract.primeiro_vencimento ? contract.primeiro_vencimento.split('T')[0] : '',
        local_assinatura: contract.local_assinatura || 'Campo Grande/MS',
        testemunhas: contract.testemunhas && Array.isArray(contract.testemunhas) && contract.testemunhas.length > 0
          ? contract.testemunhas
          : [
              { nome: contract.testemunha1_nome || 'Abel Lopes de Andrade', cpf: contract.testemunha1_cpf || '00586616209', email: contract.testemunha1_email || 'abeldiretor@yahoo.com.br' },
              { nome: contract.testemunha2_nome || 'Maikon Batista de Andrade', cpf: contract.testemunha2_cpf || '00586616209', email: contract.testemunha2_email || 'maikonandrade37@gmail.com' }
            ],
        observacoes_pagamento: contract.observacoes_pagamento || contract.payment_notes || ''
      };

      setContractData(loadedData);
      console.log('✅ Dados básicos do contrato carregados');

      // Carregar equipamentos se existirem IDs salvos
      if (contract.auxiliary_equipment_ids && Array.isArray(contract.auxiliary_equipment_ids) && contract.auxiliary_equipment_ids.length > 0) {
        console.log(`📦 Carregando ${contract.auxiliary_equipment_ids.length} equipamento(s)...`);
        await loadContractEquipment(contract.auxiliary_equipment_ids);
      } else {
        console.log('📦 Nenhum equipamento salvo neste contrato');
      }

      console.log('✅ Contrato carregado completamente');
      toast.success('Contrato carregado com sucesso');
    } catch (error) {
      console.error('❌ Erro ao carregar contrato:', error);
      toast.error('Erro ao carregar contrato');
      if (error.response?.status === 401) {
        toast.error('Sessão expirada. Faça login novamente.');
      }
    }
  };

  const handleClientChange = (clientId) => {
    // Find the selected client
    const selectedClient = clients.find(c => c.id === clientId);

    // Parse partners from client data
    let partners = [];
    if (selectedClient && selectedClient.partners) {
      try {
        // Partners may be a JSON string or already parsed object
        partners = typeof selectedClient.partners === 'string'
          ? JSON.parse(selectedClient.partners)
          : selectedClient.partners;

        // Ensure it's an array
        if (!Array.isArray(partners)) {
          partners = [];
        }

        console.log('✅ Carregados', partners.length, 'sócio(s) do cliente');
      } catch (error) {
        console.error('❌ Erro ao processar sócios:', error);
        partners = [];
      }
    }

    // Update contract data with client_id and partners
    setContractData({
      ...contractData,
      client_id: clientId,
      locatario_socios: partners
    });
  };

  const loadAllClauses = async () => {
    try {
      const response = await api.get('/api/contracts/clauses/list');
      const data = response.data;
      const allClauses = data.clauses || [];

      // Pré-selecionar TODAS as cláusulas (obrigatórias e opcionais)
      setSelectedClauses(allClauses);
      console.log(`✅ Pré-selecionadas ${allClauses.length} cláusulas automaticamente`);
    } catch (error) {
      console.error('Erro ao carregar cláusulas:', error);
      if (error.response?.status === 401) {
        toast.error('Sessão expirada. Faça login novamente.');
      }
    }
  };

  const handleCalculatedFieldsChange = (updatedData) => {
    setContractData(updatedData);
  };

  const generatePreview = async (explicitId = null) => {
    try {
      setLoading(true);
      const useId = explicitId || contractId || id;

      if (!useId) {
        toast.error('ID do contrato não disponível. Tente novamente.');
        return;
      }

      const response = await api.post(`/api/contracts/${useId}/generate-draft`, contractData);

      const data = response.data;
      if (data.customHtml) {
        setPreviewHtml(data.customHtml);
        console.log('✅ Preview usando edições salvas do banco');
      } else {
        setPreviewHtml(data.html);
      }
      setHasCustomHtml(!!data.customHtml);
      setIsEditingPreview(false);
      toast.success('Preview gerado com sucesso!');
    } catch (error) {
      console.error('Erro ao gerar preview:', error);
      const message = error.response?.data?.error || 'Erro ao gerar preview';
      toast.error(message);
      if (error.response?.status === 401) {
        toast.error('Sessão expirada. Faça login novamente.');
      }
    } finally {
      setLoading(false);
    }
  };

  // Função para baixar a minuta do contrato em DOCX
  const downloadMinutaDocx = async () => {
    try {
      const useId = contractId || (id && id !== 'new' ? id : null);

      setDownloadingDocx(true);
      toast.loading('Gerando minuta do contrato...', { id: 'docx-download' });

      // Se tem ID, usar endpoint com ID; senão usar endpoint de minuta sem ID
      let response;
      if (useId) {
        response = await api.post(`/api/contracts/${useId}/generate-docx`, contractData);
      } else {
        // Gerar minuta sem precisar salvar o contrato primeiro
        response = await api.post('/api/contracts/generate-minuta', contractData);
      }
      const { docxBase64, fileName } = response.data;

      // Converter base64 para Blob
      const byteCharacters = atob(docxBase64);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], {
        type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
      });

      // Criar link de download
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName || `Minuta_Contrato_${useId}.docx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success('Minuta baixada com sucesso!', { id: 'docx-download' });
    } catch (error) {
      console.error('Erro ao baixar minuta:', error);
      const message = error.response?.data?.error || 'Erro ao gerar minuta';
      toast.error(message, { id: 'docx-download' });
    } finally {
      setDownloadingDocx(false);
    }
  };

  // Função para baixar a minuta do contrato em PDF (mesmo visual do PDF final)
  const downloadMinutaPdf = async () => {
    try {
      const useId = contractId || (id && id !== 'new' ? id : null);

      setDownloadingPdf(true);
      setShowMinutaMenu(false);
      toast.loading('Gerando minuta em PDF...', { id: 'pdf-download' });

      let response;
      if (useId) {
        // Se tem contrato salvo, usar endpoint que gera PDF final
        response = await api.post(`/api/contracts/${useId}/generate-pdf`, contractData);
      } else {
        // Gerar minuta PDF sem precisar salvar
        response = await api.post('/api/contracts/generate-minuta-pdf', contractData);
      }
      const { pdfBase64, fileName } = response.data;

      const byteCharacters = atob(pdfBase64);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: 'application/pdf' });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName || `Minuta_Contrato_${useId || 'rascunho'}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success('Minuta PDF baixada com sucesso!', { id: 'pdf-download' });
    } catch (error) {
      console.error('Erro ao baixar minuta PDF:', error);
      const message = error.response?.data?.error || 'Erro ao gerar minuta PDF';
      toast.error(message, { id: 'pdf-download' });
    } finally {
      setDownloadingPdf(false);
    }
  };

  // Salvar HTML customizado do contrato (admin only)
  // Retorna true se salvou, false se falhou
  const saveCustomHtml = async () => {
    try {
      const useId = contractId || (id && id !== 'new' ? id : null);
      if (!useId) {
        toast.error('Salve o contrato antes de editar o preview');
        return false;
      }

      const editedHtml = editablePreviewRef.current?.innerHTML;
      if (!editedHtml) return false;

      setSavingCustomHtml(true);
      toast.loading('Salvando edições...', { id: 'save-custom' });

      // Envolver no template completo com CSS para o PDF ficar correto
      const fullHtml = `<!DOCTYPE html>
<html lang="pt-BR">
<head>
  <meta charset="UTF-8">
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body { font-family: Arial, sans-serif; font-size: 11pt; line-height: 1.6; color: #333; background: #fff; }
    .container { max-width: 800px; margin: 0 auto; padding: 20px; }
    .header { text-align: center; margin-bottom: 30px; padding-bottom: 20px; border-bottom: 3px solid #2563eb; }
    .header .company-name { font-size: 16pt; font-weight: bold; color: #1e40af; margin-bottom: 8px; }
    .header .company-info { font-size: 9pt; color: #666; line-height: 1.4; }
    .header .contract-number { font-size: 11pt; font-weight: 600; color: #1e40af; margin-top: 10px; padding: 5px 15px; background: #eff6ff; border-radius: 4px; display: inline-block; }
    .title { text-align: center; font-size: 14pt; font-weight: bold; margin: 30px 0 20px 0; text-transform: uppercase; color: #1e40af; }
    .section-label { font-size: 12pt; font-weight: bold; color: #1e40af; margin: 25px 0 10px 0; text-transform: uppercase; }
    .parties { margin: 25px 0; padding: 15px; background: #f8fafc; border-left: 4px solid #2563eb; }
    .party { margin-bottom: 15px; }
    .party:last-child { margin-bottom: 0; }
    .party-label { font-weight: bold; font-size: 11pt; color: #1e40af; text-transform: uppercase; margin-bottom: 5px; }
    .party-info { font-size: 10pt; line-height: 1.5; text-align: justify; }
    .intro { text-align: justify; margin: 20px 0; font-size: 10pt; line-height: 1.7; }
    .clause { margin: 25px 0; }
    .clause-title { font-weight: bold; font-size: 11pt; color: #1e40af; margin-bottom: 10px; text-transform: uppercase; padding-bottom: 5px; border-bottom: 1px solid #e5e7eb; page-break-after: avoid; }
    .clause-content { text-align: justify; font-size: 10pt; line-height: 1.7; margin-left: 15px; }
    .clause-content p { margin-bottom: 10px; }
    .signature-section { margin-top: 50px; page-break-inside: avoid; }
    .signature-date { text-align: center; margin: 30px 0; font-size: 11pt; }
    .signature-block { margin: 40px 0; text-align: center; }
    .signature-line { border-top: 1px solid #333; width: 300px; margin: 0 auto 5px auto; padding-top: 5px; }
    .signature-name { font-weight: bold; font-size: 10pt; }
    .signature-role { font-size: 9pt; color: #666; }
    .witnesses { margin-top: 40px; page-break-inside: avoid; }
    .witnesses-title { text-align: center; font-weight: bold; margin-bottom: 30px; }
    .witness-row { display: flex; justify-content: space-around; }
    .witness { text-align: center; flex: 1; margin: 0 20px; }
    .witness-line { border-top: 1px solid #333; margin-bottom: 5px; padding-top: 5px; }
    .witness-info { font-size: 9pt; }
    .footer-note { margin-top: 40px; padding: 15px; background: #f1f5f9; border-left: 4px solid #64748b; font-size: 8pt; color: #475569; text-align: justify; }
    table { border-collapse: collapse; }
    th, td { border: 1px solid #ccc; padding: 6px; }
  </style>
</head>
<body>
  <div class="container">${editedHtml}</div>
</body>
</html>`;

      await api.put(`/api/contracts/${useId}/custom-html`, { customHtml: fullHtml });
      setHasCustomHtml(true);
      setIsEditingPreview(false);
      toast.success('Edições salvas com sucesso!', { id: 'save-custom' });
      return true;
    } catch (error) {
      console.error('Erro ao salvar HTML customizado:', error);
      toast.error(error.response?.data?.error || 'Erro ao salvar edições', { id: 'save-custom' });
      return false;
    } finally {
      setSavingCustomHtml(false);
    }
  };

  // Restaurar template original (admin only)
  const restoreOriginalTemplate = async () => {
    try {
      const useId = contractId || (id && id !== 'new' ? id : null);
      if (!useId) return;

      if (!window.confirm('Restaurar o contrato para o template original? Todas as edições manuais serão perdidas.')) return;

      toast.loading('Restaurando template original...', { id: 'restore-template' });

      await api.delete(`/api/contracts/${useId}/custom-html`);
      setHasCustomHtml(false);
      setIsEditingPreview(false);

      // Recarregar preview com template original
      const response = await api.post(`/api/contracts/${useId}/generate-draft`, contractData);
      setPreviewHtml(response.data.html);

      toast.success('Template original restaurado!', { id: 'restore-template' });
    } catch (error) {
      console.error('Erro ao restaurar template:', error);
      toast.error('Erro ao restaurar template', { id: 'restore-template' });
    }
  };

  const validateContract = async () => {
    try {
      setLoading(true);
      const useId = contractId || id;

      const response = await api.post(`/api/contracts/${useId}/validate`, contractData);
      const data = response.data;
      setValidation(data);

      if (data.valid) {
        toast.success('Contrato validado com sucesso!');
      } else {
        toast.error(`Encontrados ${data.errors.length} erros`);
      }
    } catch (error) {
      console.error('Erro ao validar:', error);
      const message = error.response?.data?.error || 'Erro ao validar contrato';
      toast.error(message);
      if (error.response?.status === 401) {
        toast.error('Sessão expirada. Faça login novamente.');
      }
    } finally {
      setLoading(false);
    }
  };

  const sendToClicksign = async () => {
    try {
      // DEBUG: Log signers array BEFORE filtering
      console.log('🔍 TODOS OS SIGNATÁRIOS (antes de filtrar):', signers);
      console.log('📊 Total de signatários:', signers.length);

      // Validar CPFs dos signatários antes de enviar
      const signersWithEmail = signers.filter(s => s.email);

      console.log('✅ Signatários COM email:', signersWithEmail);
      console.log('❌ Signatários SEM email:', signers.filter(s => !s.email));
      console.log('📊 Total COM email:', signersWithEmail.length);

      for (const signer of signersWithEmail) {
        if (signer.cpf && !validateDocument(signer.cpf)) {
          toast.error(`CPF inválido para o signatário ${signer.name}! Por favor, corrija antes de enviar.`);
          return;
        }
      }

      setLoading(true);
      const useId = contractId || id;

      // Primeiro, salvar equipamentos se houver
      if (selectedEquipment.length > 0) {
        const equipmentData = selectedEquipment.map(eq => ({
          equipment_id: eq.equipment_id,
          quantity: eq.quantity,
          monthly_rate: eq.monthly_rate,
          start_date: contractData.start_date,
          end_date: contractData.end_date,
          total_amount: eq.total_amount
        }));

        await api.post(`/api/auxiliary-equipment/contract/${useId}`, { equipment: equipmentData });
      }

      // Enviar para Clicksign
      const response = await api.post(`/api/contracts/${useId}/send-to-clicksign`, {
        signers: signersWithEmail,
        deadline: deadline, // Prazo para assinatura
        customData: {
          ...contractData,
          equipment: selectedEquipment
        }
      });

      toast.success('Contrato enviado para o Clicksign!');
      navigate('/contracts');
    } catch (error) {
      console.error('Erro ao enviar:', error);
      const message = error.response?.data?.error || 'Erro ao enviar para Clicksign';
      toast.error(message);
      if (error.response?.status === 401) {
        toast.error('Sessão expirada. Faça login novamente.');
      }
    } finally {
      setLoading(false);
    }
  };

  const activateManually = async () => {
    try {
      if (!window.confirm('Deseja ativar este contrato manualmente?\n\nIsso irá:\n- Ativar o contrato\n- Marcar o veículo como alugado\n- Gerar todas as faturas\n- Enviar notificações ao cliente')) {
        return;
      }

      setLoading(true);
      const useId = contractId || id;

      // Salvar equipamentos se houver
      if (selectedEquipment.length > 0) {
        const equipmentData = selectedEquipment.map(eq => ({
          equipment_id: eq.equipment_id,
          quantity: eq.quantity,
          monthly_rate: eq.monthly_rate,
          start_date: contractData.start_date,
          end_date: contractData.end_date,
          total_amount: eq.total_amount
        }));
        await api.post(`/api/auxiliary-equipment/contract/${useId}`, { equipment: equipmentData });
      }

      // Ativar manualmente
      await api.post(`/api/contracts/${useId}/activate`, {
        customData: {
          ...contractData,
          equipment: selectedEquipment
        }
      });

      toast.success('Contrato ativado com sucesso!');
      navigate('/contracts');
    } catch (error) {
      console.error('Erro ao ativar:', error);
      const message = error.response?.data?.error || 'Erro ao ativar contrato';
      toast.error(message);
      if (error.response?.status === 401) {
        toast.error('Sessão expirada. Faça login novamente.');
      }
    } finally {
      setLoading(false);
    }
  };

  const createBasicContract = async () => {
    try {
      setLoading(true);

      // DEBUG: Log contractData antes de criar payload
      console.log('🔍 DEBUG - contractData:', contractData);

      // Garantir que valores undefined sejam null ao invés de string "undefined"
      const payload = {
        client_id: contractData.client_id || null,
        vehicle_id: contractData.vehicle_id || null,
        start_date: contractData.start_date || null,
        end_date: contractData.end_date || null,
        monthly_rate: contractData.monthly_rate || null,
        notes: 'Contrato criado via editor'
      };

      // DEBUG: Log payload antes de enviar
      console.log('📤 DEBUG - Payload sendo enviado:', payload);

      const response = await api.post('/api/contracts', payload);

      // DEBUG: Log resposta do backend
      console.log('📥 DEBUG - Resposta do backend:', response.data);

      const data = response.data;
      const newId = data.contract.id;
      setContractId(newId);
      toast.success('Contrato criado! Continue para as próximas etapas.');
      return newId;
    } catch (error) {
      console.error('❌ DEBUG - Erro ao criar contrato:', error);
      console.error('❌ DEBUG - Detalhes do erro:', {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message
      });
      const message = error.response?.data?.error || 'Erro ao criar contrato';
      toast.error(message);
      if (error.response?.status === 401) {
        toast.error('Sessão expirada. Faça login novamente.');
      }
      return null;
    } finally {
      setLoading(false);
    }
  };

  const saveContractData = async (useId) => {
    try {
      // Salvar TODOS os dados do contrato (incluindo custom_data)
      await api.put(`/api/contracts/${useId}`, contractData);
      console.log('✅ Dados do contrato salvos com sucesso');
      return true;
    } catch (error) {
      console.error('❌ Erro ao salvar dados do contrato:', error);
      const message = error.response?.data?.error || 'Erro ao salvar dados';
      toast.error(message);
      return false;
    }
  };

  const nextStep = async () => {
    console.log('🚀 nextStep CHAMADO!', {
      currentStep,
      isNewContract,
      contractId,
      client_id: contractData.client_id,
      vehicle_id: contractData.vehicle_id
    });

    if (currentStep === 1 && (!contractData.client_id || contractData.client_id === 'undefined')) {
      console.log('❌ Validação FALHOU: cliente não selecionado');
      toast.error('Selecione um cliente');
      return;
    }
    if (currentStep === 1 && (!contractData.vehicle_id || contractData.vehicle_id === 'undefined')) {
      toast.error('Selecione um veículo');
      return;
    }
    if (currentStep === 1 && (!contractData.start_date || contractData.start_date === 'undefined')) {
      toast.error('Selecione a data de início');
      return;
    }
    if (currentStep === 1 && (!contractData.end_date || contractData.end_date === 'undefined')) {
      toast.error('Selecione a data de término');
      return;
    }
    if (currentStep === 1 && (!contractData.monthly_rate || contractData.monthly_rate === 'undefined')) {
      toast.error('Informe o valor mensal');
      return;
    }

    // Se for novo contrato e está na etapa 1, criar o contrato básico primeiro
    let newlyCreatedId = null;
    console.log('🔍 Verificando se deve criar contrato:', {
      currentStep,
      isNewContract,
      contractId,
      condicao: currentStep === 1 && isNewContract && !contractId
    });

    if (currentStep === 1 && isNewContract && !contractId) {
      console.log('✅ Condição atendida! Chamando createBasicContract...');
      newlyCreatedId = await createBasicContract();
      if (!newlyCreatedId) {
        return; // Não avança se falhar na criação
      }
    }

    if (currentStep === 1) {
      // Salvar dados personalizados antes de gerar preview
      const useId = newlyCreatedId || contractId || id;
      const saved = await saveContractData(useId);
      if (!saved) {
        return; // Não avança se falhar ao salvar
      }

      // Ao sair da etapa 1 (indo para etapa 2), gera preview
      // Se há custom_html salvo, será priorizado automaticamente
      generatePreview(useId);
    }
    if (currentStep === 2) {
      // Auto-salvar edições do HTML antes de avançar para Step 3
      // Auto-salvar edições não salvas antes de avançar
      if (isEditingPreview && editablePreviewRef.current?.innerHTML) {
        const saved = await saveCustomHtml();
        if (!saved) {
          return;
        }
      }
      // Validar contrato
      await validateContract();
    }

    setCurrentStep(Math.min(currentStep + 1, 3));
  };

  const prevStep = () => {
    setCurrentStep(Math.max(currentStep - 1, 1));
  };

  const steps = [
    { number: 1, name: 'Dados', icon: DocumentTextIcon },
    { number: 2, name: 'Edição', icon: PencilSquareIcon },
    { number: 3, name: 'Envio', icon: PaperAirplaneIcon }
  ];

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/contracts')}
            className="flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white mb-4"
          >
            <ChevronLeftIcon className="w-5 h-5" />
            Voltar para Contratos
          </button>

          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
            {isNewContract ? 'Novo' : 'Editar'} Contrato
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-2">
            Crie e personalize contratos de locação com integração Clicksign
          </p>
        </div>

        {/* Steps */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            {steps.map((step, index) => {
              const Icon = step.icon;
              const isActive = currentStep === step.number;
              const isCompleted = currentStep > step.number;

              return (
                <React.Fragment key={step.number}>
                  <div className="flex flex-col items-center">
                    <div
                      className={`
                        w-12 h-12 rounded-full flex items-center justify-center
                        ${isActive ? 'bg-blue-600 text-white' : ''}
                        ${isCompleted ? 'bg-green-600 text-white' : ''}
                        ${!isActive && !isCompleted ? 'bg-gray-200 dark:bg-gray-700 text-gray-500' : ''}
                        transition-colors
                      `}
                    >
                      {isCompleted ? (
                        <CheckCircleIcon className="w-6 h-6" />
                      ) : (
                        <Icon className="w-6 h-6" />
                      )}
                    </div>
                    <span
                      className={`
                        mt-2 text-sm font-medium
                        ${isActive ? 'text-blue-600 dark:text-blue-400' : ''}
                        ${isCompleted ? 'text-green-600 dark:text-green-400' : ''}
                        ${!isActive && !isCompleted ? 'text-gray-500' : ''}
                      `}
                    >
                      {step.name}
                    </span>
                  </div>

                  {index < steps.length - 1 && (
                    <div
                      className={`
                        flex-1 h-1 mx-4
                        ${isCompleted ? 'bg-green-600' : 'bg-gray-200 dark:bg-gray-700'}
                        transition-colors
                      `}
                    ></div>
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </div>

        {/* Content */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
          {/* ETAPA 1: Dados */}
          {currentStep === 1 && (
            <div className="space-y-6">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                Dados do Contrato
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Cliente */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Cliente *
                  </label>
                  <SearchableSelect
                    value={contractData.client_id}
                    onChange={(client) => handleClientChange(client ? client.id : '')}
                    options={clients}
                    placeholder="Selecione um cliente"
                    searchPlaceholder="Buscar por nome, CNPJ, CPF..."
                    renderOption={(c) => `${c.name} - ${c.document}`}
                    filterFn={(c, search) => {
                      const terms = search.toLowerCase().trim().split(/\s+/);
                      const text = `${c.name} ${c.document} ${c.email || ''}`.toLowerCase();
                      return terms.every(t => text.includes(t));
                    }}
                  />
                </div>

                {/* Veículo */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Veículo *
                  </label>
                  <SearchableSelect
                    value={contractData.vehicle_id}
                    onChange={async (vehicle) => {
                      if (vehicle) {
                        if (hasCustomHtml) {
                          if (!window.confirm('Você tem edições manuais salvas no contrato. Trocar o veículo vai descartá-las. Continuar?')) {
                            return;
                          }
                        }
                        setContractData({
                          ...contractData,
                          vehicle_id: vehicle.id,
                          monthly_rate: vehicle.monthly_rate || contractData.monthly_rate
                        });
                        const useId = contractId || id;
                        if (useId && useId !== 'new' && hasCustomHtml) {
                          try {
                            await api.delete(`/api/contracts/${useId}/custom-html`);
                            setHasCustomHtml(false);
                          } catch {}
                        }
                      } else {
                        setContractData({
                          ...contractData,
                          vehicle_id: '',
                          monthly_rate: ''
                        });
                      }
                    }}
                    options={vehicles}
                    placeholder="Selecione um veículo"
                    searchPlaceholder="Buscar por marca, modelo, placa..."
                    renderOption={(v) => `${v.brand} ${v.model} - ${v.license_plate}`}
                    filterFn={(v, search) => {
                      const terms = search.toLowerCase().trim().split(/\s+/);
                      const text = `${v.brand} ${v.model} ${v.license_plate} ${v.type || ''} ${v.year || ''}`.toLowerCase();
                      return terms.every(t => text.includes(t));
                    }}
                  />
                </div>

                {/* Data Início */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Data de Início *
                  </label>
                  <input
                    type="date"
                    value={contractData.start_date}
                    onChange={(e) => setContractData({ ...contractData, start_date: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>

                {/* Data Fim */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Data de Término *
                  </label>
                  <input
                    type="date"
                    value={contractData.end_date}
                    onChange={(e) => setContractData({ ...contractData, end_date: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>

                {/* Valor Mensal */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Valor Mensal (R$) *
                  </label>
                  <input
                    type="number"
                    value={contractData.monthly_rate}
                    onChange={(e) => setContractData({ ...contractData, monthly_rate: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    placeholder="0.00"
                  />
                </div>

                {/* Desconto % */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Desconto (%)
                  </label>
                  <input
                    type="number"
                    value={contractData.discount_percentage}
                    onChange={(e) => setContractData({ ...contractData, discount_percentage: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    placeholder="0"
                  />
                </div>
              </div>

              {/* Cálculos Automáticos */}
              <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
                <CalculatedFields
                  contractData={contractData}
                  selectedEquipment={selectedEquipment}
                  onChange={handleCalculatedFieldsChange}
                />
              </div>

              {/* Identificação das Partes do Contrato */}
              <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
                <ContractParties
                  contractData={contractData}
                  selectedClient={clients.find(c => c.id === contractData.client_id)}
                  onChange={setContractData}
                />
              </div>

              {/* Informações de Pagamento */}
              <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
                <PaymentInfo
                  contractData={contractData}
                  onChange={setContractData}
                  selectedVehicle={vehicles.find(v => v.id === contractData.vehicle_id)}
                  contractNumber={contractData.contract_identifier || null}
                />
              </div>

              {/* Garantias e Política de Crédito */}
              {contractData.client_id && (
                <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
                  <GuaranteesWidget
                    clientData={clients.find(c => c.id === contractData.client_id)}
                    contractData={contractData}
                    onChange={setGuarantees}
                  />
                </div>
              )}

              {/* Informações Complementares */}
              <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                  Informações Complementares
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Valor por mm de Pneu (R$)
                    </label>
                    <input
                      type="text"
                      value={contractData.valor_por_mm_pneu}
                      onChange={(e) => setContractData({ ...contractData, valor_por_mm_pneu: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      placeholder="120,00"
                    />
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      Valor cobrado por milímetro de desgaste de pneu na devolução
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Odômetro do Veículo (km)
                    </label>
                    <input
                      type="number"
                      value={contractData.veiculo_km_inicial}
                      onChange={(e) => setContractData({ ...contractData, veiculo_km_inicial: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      placeholder="0"
                    />
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      Quilometragem atual do veículo no momento da locação
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ETAPA 2: Edição */}
          {currentStep === 2 && (
            <div className="space-y-6">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                Cláusulas do Contrato
              </h2>

              <ClauseSelector
                selectedClauses={selectedClauses}
                onChange={setSelectedClauses}
              />

              {/* Preview em Tempo Real */}
              <div className="mt-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                      <EyeIcon className="w-5 h-5" />
                      Preview do Contrato
                    </h3>
                    {previewLoading && (
                      <span className="flex items-center gap-1 text-sm text-blue-600 dark:text-blue-400">
                        <ArrowPathIcon className="w-4 h-4 animate-spin" />
                        Atualizando...
                      </span>
                    )}
                    {lastPreviewTime && !previewLoading && (
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        Atualizado às {lastPreviewTime.toLocaleTimeString('pt-BR')}
                      </span>
                    )}
                  </div>

                  <div className="flex items-center gap-4">
                    {/* Toggle Auto-Preview */}
                    <label className="flex items-center gap-2 cursor-pointer">
                      <span className="text-sm text-gray-600 dark:text-gray-400">Auto-preview</span>
                      <button
                        type="button"
                        onClick={() => setAutoPreviewEnabled(!autoPreviewEnabled)}
                        className={`
                          relative inline-flex h-6 w-11 items-center rounded-full transition-colors
                          ${autoPreviewEnabled ? 'bg-blue-600' : 'bg-gray-300 dark:bg-gray-600'}
                        `}
                      >
                        <span
                          className={`
                            inline-block h-4 w-4 transform rounded-full bg-white transition-transform
                            ${autoPreviewEnabled ? 'translate-x-6' : 'translate-x-1'}
                          `}
                        />
                      </button>
                    </label>

                    {/* Botão Atualizar Manual */}
                    <button
                      type="button"
                      onClick={() => generatePreview()}
                      disabled={previewLoading}
                      className="flex items-center gap-2 px-3 py-1.5 text-sm bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-50 transition-colors"
                    >
                      <ArrowPathIcon className={`w-4 h-4 ${previewLoading ? 'animate-spin' : ''}`} />
                      Atualizar
                    </button>

                    {/* Botão Baixar Minuta com dropdown */}
                    <div className="relative" ref={minutaMenuRef}>
                      <button
                        type="button"
                        onClick={() => setShowMinutaMenu(!showMinutaMenu)}
                        disabled={(downloadingDocx || downloadingPdf) || !previewHtml}
                        className={`flex items-center gap-2 px-3 py-1.5 text-sm rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors ${isRegenerateMode ? 'bg-violet-600 hover:bg-violet-700 text-white ring-2 ring-violet-400 ring-offset-1' : 'bg-green-600 hover:bg-green-700 text-white'}`}
                        title="Baixar minuta do contrato"
                      >
                        <DocumentArrowDownIcon className={`w-4 h-4 ${(downloadingDocx || downloadingPdf) ? 'animate-pulse' : ''}`} />
                        {downloadingDocx ? 'Gerando DOCX...' : downloadingPdf ? 'Gerando PDF...' : isRegenerateMode ? '🔄 Regerar PDF' : 'Baixar Minuta'}
                        <svg className="w-3 h-3 ml-1" fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" /></svg>
                      </button>
                      {showMinutaMenu && (
                        <div className="absolute right-0 mt-1 w-48 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-50 overflow-hidden">
                          <button
                            type="button"
                            onClick={() => { setShowMinutaMenu(false); downloadMinutaPdf(); }}
                            className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 dark:text-gray-200 hover:bg-green-50 dark:hover:bg-green-900/30 transition-colors"
                          >
                            <svg className="w-4 h-4 text-red-500" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" /></svg>
                            PDF
                          </button>
                          <button
                            type="button"
                            onClick={() => { setShowMinutaMenu(false); downloadMinutaDocx(); }}
                            className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 dark:text-gray-200 hover:bg-green-50 dark:hover:bg-green-900/30 transition-colors border-t border-gray-100 dark:border-gray-700"
                          >
                            <svg className="w-4 h-4 text-blue-500" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" /></svg>
                            Word (DOCX)
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Barra de edição (admin only) */}
                {isAdmin && previewHtml && (contractId || (id && id !== 'new')) && (
                  <div className={`flex items-center justify-between px-4 py-2 rounded-t-lg border border-b-0 ${isEditingPreview ? 'bg-amber-50 dark:bg-amber-900/20 border-amber-300 dark:border-amber-600' : 'bg-gray-50 dark:bg-gray-800 border-gray-300 dark:border-gray-600'}`}>
                    <div className="flex items-center gap-2">
                      {isEditingPreview ? (
                        <>
                          <PencilSquareIcon className="w-4 h-4 text-amber-600" />
                          <span className="text-sm font-medium text-amber-700 dark:text-amber-400">Modo edição ativo - clique no texto para editar</span>
                        </>
                      ) : (
                        <span className={`text-xs ${hasCustomHtml ? 'text-green-600 dark:text-green-400 font-medium' : 'text-gray-500 dark:text-gray-400'}`}>
                          {hasCustomHtml ? '✓ Edições manuais salvas — este HTML será enviado para assinatura' : 'Admin: você pode editar o texto diretamente'}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      {isEditingPreview ? (
                        <>
                          <button
                            type="button"
                            onClick={saveCustomHtml}
                            disabled={savingCustomHtml}
                            className="flex items-center gap-1 px-3 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50 transition-colors"
                          >
                            {savingCustomHtml ? 'Salvando...' : 'Salvar Edições'}
                          </button>
                          <button
                            type="button"
                            onClick={() => { setIsEditingPreview(false); generatePreview(); }}
                            className="flex items-center gap-1 px-3 py-1 text-xs bg-gray-500 text-white rounded hover:bg-gray-600 transition-colors"
                          >
                            Cancelar
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            type="button"
                            onClick={() => setIsEditingPreview(true)}
                            className="flex items-center gap-1 px-3 py-1 text-xs bg-amber-500 text-white rounded hover:bg-amber-600 transition-colors"
                          >
                            <PencilSquareIcon className="w-3 h-3" />
                            Editar Texto
                          </button>
                          {hasCustomHtml && (
                            <button
                              type="button"
                              onClick={restoreOriginalTemplate}
                              className="flex items-center gap-1 px-3 py-1 text-xs bg-red-500 text-white rounded hover:bg-red-600 transition-colors"
                            >
                              Restaurar Original
                            </button>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                )}

                {previewHtml ? (
                  isEditingPreview ? (
                    // Modo edição: div contentEditable (sem estilos completos, apenas para editar texto)
                    <div
                      ref={(el) => { previewRef.current = el; editablePreviewRef.current = el; }}
                      contentEditable={true}
                      suppressContentEditableWarning={true}
                      className={`
                        border border-amber-400 dark:border-amber-500 ring-2 ring-amber-200 dark:ring-amber-800
                        p-6 bg-white dark:bg-gray-900 outline-none cursor-text
                        max-h-[600px] overflow-y-auto transition-opacity duration-200
                        ${isAdmin && previewHtml && (contractId || (id && id !== 'new')) ? 'rounded-b-lg' : 'rounded-lg'}
                        ${previewLoading ? 'opacity-60' : 'opacity-100'}
                      `}
                      dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(previewHtml, { ALLOWED_TAGS: ['style', 'h1', 'h2', 'h3', 'h4', 'p', 'br', 'strong', 'b', 'i', 'em', 'u', 'ul', 'ol', 'li', 'table', 'thead', 'tbody', 'tr', 'th', 'td', 'div', 'span'], ALLOWED_ATTR: ['class', 'style'], FORCE_BODY: true }) }}
                    />
                  ) : (
                    // Modo visualização: iframe com HTML completo (preserva <head> e <style>)
                    <iframe
                      ref={(el) => { previewRef.current = el; }}
                      srcDoc={previewHtml}
                      className={`
                        border border-gray-300 dark:border-gray-600 w-full bg-white
                        max-h-[600px] overflow-y-auto transition-opacity duration-200
                        ${isAdmin && previewHtml && (contractId || (id && id !== 'new')) ? 'rounded-b-lg' : 'rounded-lg'}
                        ${previewLoading ? 'opacity-60' : 'opacity-100'}
                      `}
                      style={{ height: '600px', border: 'none' }}
                      title="Preview do Contrato"
                    />
                  )
                ) : (
                  <div className="border border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-12 text-center">
                    <EyeIcon className="w-12 h-12 mx-auto text-gray-400 mb-4" />
                    <p className="text-gray-500 dark:text-gray-400">
                      O preview do contrato será gerado automaticamente quando os dados forem preenchidos
                    </p>
                    <button
                      type="button"
                      onClick={() => generatePreview()}
                      className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      Gerar Preview Agora
                    </button>
                  </div>
                )}

                {/* Dica sobre preview em tempo real */}
                {autoPreviewEnabled && (
                  <p className="mt-2 text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1">
                    <span className="inline-block w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
                    Preview em tempo real ativado - O contrato será atualizado automaticamente quando você alterar os dados
                  </p>
                )}
              </div>
            </div>
          )}

          {/* ETAPA 3: Envio */}
          {currentStep === 3 && (
            <div className="space-y-6">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                Validação e Envio
              </h2>

              <ValidationChecklist
                checklist={validation.checklist}
                errors={validation.errors}
                warnings={validation.warnings}
              />

              {/* Prazo de Assinatura */}
              <div className="mb-6 p-6 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                  ⏰ Prazo para Assinatura
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                  Defina até quando os signatários devem assinar o contrato. Após essa data, o documento expira.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      📅 Data Limite
                    </label>
                    <input
                      type="date"
                      value={deadline}
                      onChange={(e) => setDeadline(e.target.value)}
                      min={new Date().toISOString().split('T')[0]}
                      className="w-full px-4 py-3 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="flex-1">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        ⚡ Atalhos Rápidos
                      </label>
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={() => {
                            const date = new Date();
                            date.setDate(date.getDate() + 3);
                            setDeadline(date.toISOString().split('T')[0]);
                          }}
                          className="px-3 py-2 text-xs font-medium bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
                        >
                          3 dias
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            const date = new Date();
                            date.setDate(date.getDate() + 7);
                            setDeadline(date.toISOString().split('T')[0]);
                          }}
                          className="px-3 py-2 text-xs font-medium bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
                        >
                          7 dias
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            const date = new Date();
                            date.setDate(date.getDate() + 15);
                            setDeadline(date.toISOString().split('T')[0]);
                          }}
                          className="px-3 py-2 text-xs font-medium bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
                        >
                          15 dias
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            const date = new Date();
                            date.setDate(date.getDate() + 30);
                            setDeadline(date.toISOString().split('T')[0]);
                          }}
                          className="px-3 py-2 text-xs font-medium bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
                        >
                          30 dias
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
                {deadline && (
                  <div className="mt-3 text-sm text-gray-600 dark:text-gray-400">
                    📌 O contrato deve ser assinado até <strong>{new Date(deadline + 'T00:00:00').toLocaleDateString('pt-BR', { day: '2-digit', month: 'long', year: 'numeric' })}</strong>
                  </div>
                )}
              </div>

              {/* Signatários */}
              <SignersList
                contractData={contractData}
                selectedClient={clients.find(c => c.id === contractData.client_id)}
                signers={signers}
                onChange={setSigners}
              />
            </div>
          )}

          {/* Navigation */}
          <div className="flex justify-between mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
            <button
              onClick={prevStep}
              disabled={currentStep === 1}
              className="flex items-center gap-2 px-6 py-3 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronLeftIcon className="w-5 h-5" />
              Anterior
            </button>

            {currentStep < 3 ? (
              <button
                onClick={nextStep}
                className="flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Próximo
                <ChevronRightIcon className="w-5 h-5" />
              </button>
            ) : (
              <div className="flex items-center gap-3">
                <button
                  onClick={activateManually}
                  disabled={loading}
                  className="flex items-center gap-2 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {loading ? (
                    <>Ativando...</>
                  ) : (
                    <>
                      <CheckBadgeIcon className="w-5 h-5" />
                      Ativar Manualmente
                    </>
                  )}
                </button>
                <button
                  onClick={sendToClicksign}
                  disabled={!validation.valid || loading}
                  className="flex items-center gap-2 px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {loading ? (
                    <>Enviando...</>
                  ) : (
                    <>
                      <PaperAirplaneIcon className="w-5 h-5" />
                      Enviar para Clicksign
                    </>
                  )}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContractEditorPage;